import { Popover, cn } from '@nexo/ui';
import { Search, Smile } from 'lucide-react';
import { useState } from 'react';
import { emojiCategories, searchEmojis } from './emoji';
import { useTranslation } from 'react-i18next';

/** Emoji button + searchable picker. */
export function EmojiPicker({
  onPick,
  align = 'left',
  size = 'md',
  label,
  placement = 'top',
}: {
  onPick: (emoji: string) => void;
  align?: 'left' | 'right';
  size?: 'sm' | 'md';
  label?: string;
  /** Open above (composer) or below (message actions near the top of the list). */
  placement?: 'top' | 'bottom';
}) {
  const [query, setQuery] = useState('');
  const { t } = useTranslation();
  const results = query ? searchEmojis(query) : null;
  return (
    <Popover
      align={align}
      className={cn('w-80 p-2', placement === 'top' && 'bottom-full top-auto! mt-0! mb-2')}
      trigger={({ toggle, open }) => (
        <button
          type="button"
          aria-label={label ?? t('Emojis')}
          title={label ?? t('Emojis')}
          onClick={toggle}
          className={cn(
            'inline-flex items-center justify-center rounded-lg text-muted hover:bg-surface-2 hover:text-text',
            size === 'sm' ? 'size-7 [&_svg]:size-4' : 'size-8.5 [&_svg]:size-[18px]',
            open && 'bg-accent-soft text-accent',
          )}
        >
          <Smile />
        </button>
      )}
    >
      {({ close }) => (
        <div>
          <label className="relative mb-2 block">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-subtle" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('Buscar emoji (fiesta, ok, cohete…)')}
              aria-label={t('Buscar emoji')}
              className="h-8 w-full rounded-lg bg-surface-2 pr-2 pl-8 text-sm ring-1 ring-inset ring-border focus:ring-accent focus:outline-none"
            />
          </label>
          <div className="scrollbar-thin max-h-64 overflow-y-auto pr-1">
            {(results
              ? [
                  {
                    id: 'resultados',
                    label: t('{{count}} resultados', { count: results.length }),
                    emojis: results,
                  },
                ]
              : emojiCategories
            ).map((category) => (
              <section key={category.id} className="mb-2">
                <h4 className="px-1 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
                  {results ? category.label : t(category.label)}
                </h4>
                <div className="grid grid-cols-8 gap-0.5">
                  {category.emojis.map((emoji) => (
                    <button
                      key={emoji.char}
                      type="button"
                      title={emoji.keywords}
                      onClick={() => {
                        onPick(emoji.char);
                        close();
                        setQuery('');
                      }}
                      className="flex size-8 items-center justify-center rounded-md text-xl hover:bg-surface-2"
                    >
                      {emoji.char}
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}
